import { GroupedTasks, Task, TaskList } from "@/context/data.types";
import { handleApiError } from "@/hooks/useApiErrorHandler";
import { productService, vendorGuestService } from "@/services";
import { TASK_PAGE_SIZE } from "@/constants/taskConstants";
// import VendorManagementTasks from "@/utils/json/SampleTaskList.json";

// Types for new API responses (exported for callers that need to pass cached counts)
export interface GroupCountResponse {
  count: number;
  objects: Array<{
    groupName: string;
    tasksCount: number;
  }>;
}

interface TaskListResponse {
  pageNo: number;
  pageSize: number;
  count: number;
  hasMore: boolean;
  objects: Task[];
}

export const getAggregatedTaskList = (
  tasksListArr: Task[],
  aggregatedTaskList: Task[],
  isVendor: boolean,
) => {
  if (tasksListArr?.length) {
    let newTasks: Task[] = [];
    // Create a set to track the task ids we've already seen
    const existingTaskIds = new Set(aggregatedTaskList.map((task) => task?.id));
    if (existingTaskIds) {
      // Filter out tasks that are already in the aggregatedTaskList
      newTasks = tasksListArr.filter((task) => !existingTaskIds.has(task?.id));
      // Append the unique tasks to the aggregatedTaskList
      aggregatedTaskList = [...aggregatedTaskList, ...newTasks];
    }
    // Group tasks by category
    const groupedTasks: GroupedTasks = aggregatedTaskList.reduce(
      (acc, task) => {
        const category =
          task?.taskCategory ?? task?.title?.split("/")?.[0] ?? "Uncategorized";
        // "title": "Vendor Onboarding/Vendor setup in GOLD",
        if (!acc[category]) {
          acc[category] = [];
        }
        acc[category].push(task);
        return acc;
      },
      {} as GroupedTasks
    );
    // Update the content with grouped tasks
    const updatedContent = Object.keys(groupedTasks).map((category) => {
      const tasksForCategory = groupedTasks[category] || [];
      return {
        contentTitle: category,
        importantBool: false,
        count: tasksForCategory.length,
        tasks: tasksForCategory, // Add tasks for the category
      };
    });

    const totalTaskCount = aggregatedTaskList.length;

    return {
      taskCount: totalTaskCount, // Include VendorManagementTasks count only if isVendor
      taskList: updatedContent
    };
  }
};

/**
 * Fetch tasks for a specific group with pagination
 * @param value - User identifier (VENDOR or userId)
 * @param groupName - Name of the group to fetch tasks for
 * @param pageNo - Page number (0-indexed)
 * @param aggregatedTaskList - Current aggregated task list for deduplication
 * @param search - Optional search query for server-side filtering
 * @param filterParams - Optional filter parameters for server-side filtering
 * @returns Object with tasks, hasMore flag, and total count
 */
export const fetchTasksForGroup = async (
  value: string,
  groupName: string,
  pageNo: number = 0,
  aggregatedTaskList: Task[] = [],
  search?: string,
  filterParams?: {
    assigneeUserId?: string[];
    assigneeRoleId?: string[];
    creationDate?: {
      startDate?: string;
      endDate?: string;
    };
  }
): Promise<{ tasks: Task[]; hasMore: boolean; totalCount: number }> => {
  try {
    let taskListResponse: TaskListResponse;
    const fetchOptions: any = {
      pageNo,
      pageSize: TASK_PAGE_SIZE,
      groupName: groupName,
    };
    
    // Add search parameter if provided
    if (search && search.trim()) {
      fetchOptions.search = search.trim();
    }
    
    // Add filter parameters if provided
    if (filterParams) {
      if (filterParams.assigneeUserId && filterParams.assigneeUserId.length > 0) {
        fetchOptions.assigneeUserId = filterParams.assigneeUserId;
      }
      if (filterParams.assigneeRoleId && filterParams.assigneeRoleId.length > 0) {
        fetchOptions.assigneeRoleId = filterParams.assigneeRoleId;
      }
      if (filterParams.creationDate) {
        fetchOptions.creationDate = filterParams.creationDate;
      }
    }
    
    if (value === "VENDOR") {
      taskListResponse = await vendorGuestService.fetchVendorTasksGeneric(fetchOptions);
    } else {
      taskListResponse = await productService.fetchTasksGeneric(fetchOptions);
    }

    const tasks = taskListResponse?.objects || [];
    
    // Deduplicate tasks only if we're loading more pages (pageNo > 0) and not when filtering/searching
    // When filters or search are applied, we should replace tasks, not deduplicate
    // When pageNo === 0 (first page/group switch), we should NOT deduplicate - always return fresh data
    // Note: search can be undefined (not provided), empty string (clearing search), or a value (searching)
    // We should NOT deduplicate when search is explicitly provided (even if empty string)
    let processedTasks = tasks;
    // Only deduplicate if:
    // 1. We're loading more pages (pageNo > 0) - pagination within the same group
    // 2. No search or filters are applied (search === undefined && !filterParams)
    // This ensures that when switching groups (pageNo === 0), we always get fresh data
    if (pageNo > 0 && aggregatedTaskList.length > 0 && search === undefined && !filterParams) {
      // Only deduplicate when loading more pages without filters/search
      const existingTaskIds = new Set(aggregatedTaskList.map((task) => task?.id));
      processedTasks = tasks.filter((task) => !existingTaskIds.has(task?.id));
    }
    
    return {
      tasks: processedTasks,
      hasMore: taskListResponse?.hasMore ?? false,
      totalCount: taskListResponse?.count ?? 0,
    };
  } catch (error) {
    handleApiError(error, "Something went wrong while fetching tasks for group");
    throw new Error("Something went wrong while fetching tasks for group");
  }
};

/**
 * Fetch only group counts (for dashboard)
 */
export const fetchTaskGroupCountsOnly = async (
  value: string
): Promise<{ taskCount: number }> => {
  try {
    let groupCountResponse: GroupCountResponse;
    if (value === "VENDOR") {
      groupCountResponse = (await vendorGuestService.fetchVendorTaskGroupCounts()) as GroupCountResponse;
    } else {
      groupCountResponse = (await productService.fetchTaskGroupCounts()) as GroupCountResponse;
    }

    if (!groupCountResponse || !groupCountResponse.objects) {
      return { taskCount: 0 };
    }

    // Calculate total count from API response
    const totalTaskCount = groupCountResponse.objects.reduce(
      (sum, group) => sum + group.tasksCount,
      0
    );

    return { taskCount: totalTaskCount };
  } catch (error) {
    handleApiError(error, "Something went wrong while fetching task counts");
    throw new Error("Something went wrong while fetching task counts");
  }
};

/** Return type when group counts are fetched (so caller can pass as cache) */
export type FetchTaskListResult = {
  taskList: TaskList;
  taskCount: number;
  groupCountResponse?: GroupCountResponse;
};

/**
 * Fetch task list using new generic APIs
 * Fetches group counts and initial page of tasks for each group
 * @param groupNameToFetch - Optional group name to fetch tasks for. If not provided, fetches "Vendor assessment" or first group as fallback
 * @param cachedGroupCountResponse - Optional: when provided with fetchTasks true, skips the groupCount API call (avoids duplicate call when caller already has counts)
 */
export const fetchTaskListNew = async (
  value: string,
  aggregatedTaskList: Task[],
  isUserId = false,
  userRoles?: any[],
  fetchTasks: boolean = true,
  groupNameToFetch?: string,
  cachedGroupCountResponse?: GroupCountResponse
): Promise<FetchTaskListResult> => {
  let taskData: FetchTaskListResult = {
    taskList: [],
    taskCount: 0,
  };
  let isVendor: boolean = false;
  
  try {
    // Step 1: Fetch group counts (or use cached when provided to avoid duplicate API call on My Tasks)
    let groupCountResponse: GroupCountResponse | undefined = cachedGroupCountResponse;
    if (!groupCountResponse) {
      if (value === "VENDOR") {
        groupCountResponse = (await vendorGuestService.fetchVendorTaskGroupCounts()) as GroupCountResponse;
        isVendor = true;
      } else {
        groupCountResponse = (await productService.fetchTaskGroupCounts()) as GroupCountResponse;
      }
    } else {
      isVendor = value === "VENDOR";
    }

    if (!groupCountResponse || !groupCountResponse.objects) {
      return taskData;
    }

    // Calculate total count from API response
    const totalTaskCount = groupCountResponse.objects.reduce(
      (sum, group) => sum + group.tasksCount,
      0
    );

    // If only counts needed (for dashboard), return early without fetching tasks (include groupCountResponse so caller can cache)
    if (fetchTasks === false) {
      return {
        taskCount: totalTaskCount,
        taskList: groupCountResponse.objects.map((group) => ({
          contentTitle: group.groupName,
          importantBool: false,
          count: group.tasksCount,
          tasks: [],
          currentPage: 0,
          hasMore: group.tasksCount > 0,
        })),
        groupCountResponse,
      };
    }

    // Step 2: Fetch first page of tasks for the specified group (or fallback to "Vendor assessment" or first group)
    let groupToFetch = groupNameToFetch;
    
    // If no group specified, determine which group to fetch:
    // 1. Try "Vendor assessment" if it exists
    // 2. Otherwise, use the first group
    if (!groupToFetch) {
      const vendorAssessmentGroup = groupCountResponse.objects.find(
        (group) => group.groupName === "Vendor assessment"
      );
      groupToFetch = vendorAssessmentGroup 
        ? "Vendor assessment" 
        : groupCountResponse.objects[0]?.groupName;
    }
    
    const allTasks: Task[] = [];
    
    // Fetch tasks for the determined group
    if (groupToFetch) {
      const targetGroup = groupCountResponse.objects.find(
        (group) => group.groupName === groupToFetch
      );
      if (targetGroup) {
        const result = await fetchTasksForGroup(value, groupToFetch, 0, aggregatedTaskList);
        allTasks.push(...result.tasks);
      }
    }

    // Step 3: Transform to match existing structure
    // Since we're fetching tasks for a specific group, assign them directly to that group
    // Group tasks by category (using taskCategory from API) for safety
    const groupedTasks: GroupedTasks = {};
    
    if (allTasks.length > 0) {
      // Group fetched tasks by category
      allTasks.forEach((task) => {
        const category = task?.taskCategory ?? task?.title?.split("/")?.[0] ?? "Uncategorized";
        if (!groupedTasks[category]) {
          groupedTasks[category] = [];
        }
        groupedTasks[category].push(task);
      });
    }

    // Update aggregated list with new tasks
    const existingTaskIds = new Set(aggregatedTaskList.map((task) => task?.id));
    const newTasks = allTasks.filter((task) => !existingTaskIds.has(task?.id));
    const updatedAggregatedList = [...aggregatedTaskList, ...newTasks];

    // Create task list structure matching group counts from API
    const updatedContent = groupCountResponse.objects.map((group) => {
      // For the group we fetched, use the fetched tasks directly
      // For other groups, use grouped tasks or empty array
      let tasksForCategory: Task[] = [];
      if (group.groupName === groupToFetch && allTasks.length > 0) {
        // Use fetched tasks directly for the fetched group
        tasksForCategory = allTasks;
      } else {
        // For other groups, try to match by category name
        tasksForCategory = groupedTasks[group.groupName] || [];
      }
      
      const tasksCount = tasksForCategory.length;
      const totalCount = group.tasksCount;
      
      return {
        contentTitle: group.groupName,
        importantBool: false,
        count: totalCount, // Use count from API
        tasks: tasksForCategory,
        currentPage: 0,
        hasMore: tasksCount < totalCount,
      };
    });

    taskData = {
      taskCount: totalTaskCount,
      taskList: updatedContent,
    };
  } catch (error) {
    handleApiError(error, "Something went wrong while fetching tasklist");
    throw new Error("Something went wrong while fetching tasklist");
  }
  
  return taskData;
};


/**
 * Fetch task list using new generic APIs only
 * @param cachedGroupCountResponse - Optional: when provided with fetchTasks true, skips the groupCount API call
 */
export const fetchTaskList = async (
  value: string,
  aggregatedTaskList: Task[],
  isUserId = false,
  userRoles?: any[],
  fetchTasks: boolean = true,
  groupNameToFetch?: string,
  cachedGroupCountResponse?: GroupCountResponse
): Promise<FetchTaskListResult> => {
  return await fetchTaskListNew(value, aggregatedTaskList, isUserId, userRoles, fetchTasks, groupNameToFetch, cachedGroupCountResponse);
};